// Export / import a player record as a JSON file. Used by the player-side
// "💾 Export" and "📂 Import" actions, and by the DM-side history-restore
// path (same shape, different transport).
//
// What we strip on export:
//   lastSeen, connected, attentive  — heartbeat fields, meaningless across sessions
//   tokenId                          — a stale token reference is worse than no reference
// Everything else (charName, class, level, abilityScores, inventory, currency,
// paperdoll, weaponProficiencies, ac, etc.) is preserved verbatim.

const RUNTIME_FIELDS = new Set(['lastSeen', 'connected', 'attentive', 'tokenId']);

const SCHEMA_VERSION = 1;

export function sanitizeForBackup(record) {
  if (!record || typeof record !== 'object') return null;
  const out = {};
  for (const [k, v] of Object.entries(record)) {
    if (RUNTIME_FIELDS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

export function exportToBlob(record) {
  const sanitized = sanitizeForBackup(record);
  const wrapper = {
    schema:    'dndvtt-character',
    version:   SCHEMA_VERSION,
    exportedAt: Date.now(),
    record:    sanitized,
  };
  const text = JSON.stringify(wrapper, null, 2);
  return new Blob([text], { type: 'application/json' });
}

export function downloadCharacter(record) {
  const blob  = exportToBlob(record);
  const url   = URL.createObjectURL(blob);
  const date  = new Date().toISOString().slice(0, 10);
  const name  = (record?.charName || 'character').replace(/[^a-z0-9-_]+/gi, '-');
  const a     = document.createElement('a');
  a.href      = url;
  a.download  = `${name}-${date}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 200);
}

export function parseImportedJson(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    return { ok: false, error: 'File is not valid JSON.' };
  }
  // Support both: a wrapper object {record:{...}} (our preferred export shape)
  // or a bare record object (forwards-compatible if someone hand-edits).
  const record = parsed?.record ?? parsed;
  const validation = validatePlayerJson(record);
  if (!validation.ok) return validation;
  return { ok: true, data: sanitizeForBackup(record) };
}

export function validatePlayerJson(record) {
  if (!record || typeof record !== 'object') {
    return { ok: false, error: 'Top-level value is not an object.' };
  }
  if (!record.charName || typeof record.charName !== 'string') {
    return { ok: false, error: 'Missing charName.' };
  }
  // Light sanity checks — don't reject older or partial records, just flag bad shapes.
  if (record.abilityScores && typeof record.abilityScores !== 'object') {
    return { ok: false, error: 'abilityScores must be an object.' };
  }
  if (record.inventory && !Array.isArray(record.inventory)) {
    return { ok: false, error: 'inventory must be an array.' };
  }
  return { ok: true };
}

// One-line summary used for confirm dialogs ("Import 'Aria' (Fighter L5)?").
export function summarize(record) {
  if (!record) return '(empty)';
  const name = record.charName || 'Unnamed';
  const cls  = record.class || '—';
  const lvl  = record.level || 1;
  return `${name} (${cls} L${lvl})`;
}
